import type { Preset } from '../../core/types';
import type { ComponentMetaInput } from '../../ui';
import { AUTOMATION_SECTION_HEIGHT, AUTOMATION_TOP } from './layout';
import { roleVoiceMode } from '../mimo/roleVoiceStore';

export interface InitiativeVisibility { image: boolean; tts: boolean; }
const ALL_VISIBLE: InitiativeVisibility = { image: true, tts: true };

export function roleVoiceEditorHeight(preset: Preset): number {
  const mode = roleVoiceMode(preset);
  return !preset.ttsProviderId || !preset.ttsModel || mode === 'preset' ? 0 : mode === 'clone' ? 368 : 240;
}

export function roleVoiceAutoDesignMeta(top = AUTOMATION_TOP, visibility: InitiativeVisibility = ALL_VISIBLE) {
  const y = top + 456 - (visibility.image ? 0 : 74) - (visibility.tts ? 0 : 76);
  return {
    label: {
      id: 'presets.editor.automation.autoVoiceDesignLabel', page: 'presets', role: 'text', label: '自动音色设计标签',
      layout: { x: 20, y: y + 4, width: 250, height: 24 }, zIndex: 6,
      notes: '音色设计或复刻模式显示；安卓用 TextView',
    },
    switch: {
      id: 'presets.editor.automation.autoVoiceDesignSwitch', page: 'presets', role: 'button', label: '自动音色设计',
      layout: { x: 300, y, width: 62, height: 32 }, zIndex: 6, props: ['checked'],
      notes: '左右拨动胶囊开关；默认关闭；安卓用 SwitchMaterial，绑定 ttsAutoVoiceDesign',
    },
  } satisfies Record<string, ComponentMetaInput>;
}

export function automationEditorHeight(preset: Preset, visibility: InitiativeVisibility = ALL_VISIBLE): number {
  return AUTOMATION_SECTION_HEIGHT + (preset.quietPeriods?.length ?? 0) * 52
    + roleVoiceEditorHeight(preset)
    - (visibility.image ? 0 : 74) - (visibility.tts ? 0 : 76);
}

export function automationEditorMeta(top = AUTOMATION_TOP, height = AUTOMATION_SECTION_HEIGHT, visibility: InitiativeVisibility = ALL_VISIBLE) {
  const imageOffset = visibility.image ? 0 : 74;
  return {
    card: {
      id: 'presets.editor.automation.card', page: 'presets', role: 'container', label: '自动化与媒体设置卡片',
      layout: { x: 8, y: top, width: 374, height }, zIndex: 4,
      notes: '角色自动化、勿扰时段与媒体提供商设置',
    },
    title: {
      id: 'presets.editor.automation.title', page: 'presets', role: 'text', label: '自动化与媒体标题',
      layout: { x: 20, y: top + 12, width: 220, height: 24 }, zIndex: 6,
      notes: '自动化与媒体区块标题；安卓用 TextView',
    },
    imagePolicyLabel: {
      id: 'presets.editor.automation.imagePolicyLabel', page: 'presets', role: 'text', label: '生图积极性标签',
      layout: { x: 20, y: top + 226, width: 220, height: 18 }, zIndex: 6,
      notes: '角色生图积极性标签；安卓用 TextInputLayout hint',
    },
    imagePolicy: {
      id: 'presets.editor.automation.imagePolicy', page: 'presets', role: 'input', label: '生图积极性策略',
      layout: { x: 20, y: top + 246, width: 342, height: 40 }, zIndex: 6,
      props: ['value', 'disabled'], notes: '管理员开启生图积极性后显示；主用户要求、主角色依据自身设定决定、角色决定或用户要求；安卓用 Spinner',
    },
    ttsPolicyLabel: {
      id: 'presets.editor.automation.ttsPolicyLabel', page: 'presets', role: 'text', label: '语音 TTS 积极性标签',
      layout: { x: 20, y: top + 380 - imageOffset, width: 250, height: 18 }, zIndex: 6,
      notes: '角色语音 TTS 积极性标签；安卓用 TextInputLayout hint',
    },
    ttsPolicy: {
      id: 'presets.editor.automation.ttsPolicy', page: 'presets', role: 'input', label: '语音 TTS 积极性策略',
      layout: { x: 20, y: top + 400 - imageOffset, width: 342, height: 40 }, zIndex: 6,
      props: ['value', 'disabled'], notes: '管理员开启语音积极性后显示；主用户要求、主角色依据自身设定决定、角色决定或用户要求；安卓用 Spinner',
    },
  } satisfies Record<string, ComponentMetaInput>;
}
